import CircularProgress from "@material-ui/core/CircularProgress";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import workspaceDashboard from "app/api/workspaceDashboardApi";
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_BACKEND_SIDE,
} from "app/constant/date-time-format";
import { Chart } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import zoomPlugin from "chartjs-plugin-zoom";
import _ from "lodash";
import _debounce from "lodash/debounce";
import moment from "moment";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { useResizeDetector } from "react-resize-detector";
import { setChartRange } from "../../../../redux/actions/chartRangeActions";
import { formatString } from "../../../../utils/string-formatter";
import { useLanguage } from "../../../hooks";
import Slider from "../Slider";
import "./styles.css";

const initial = {
  min: moment().subtract(1, "years").startOf("year").valueOf(),
  max: moment().endOf("year").valueOf(),
  start: moment().subtract(1, "months").valueOf(),
  end: moment().add(1, "months").valueOf(),
  minRange: 1 * 60 * 60 * 1000,
};

Chart.register(annotationPlugin);
Chart.register(zoomPlugin);

const lineDash = {
  1: [1, 1],
  2: [10, 5],
  3: [10, 3, 3, 3],
  4: [20, 5],
  5: [20, 3, 3, 3],
};

const plugin = {
  id: "plugin",
  afterDatasetDraw: function (chart, args) {
    if (args.meta._dataset.isCustomLineColumn) {
      args.meta.data.forEach(function (element) {
        const borderWidth = args.meta._dataset.realBorderWidth;
        const ctx = chart.ctx;
        const vm = element;
        const half = vm.width / 2;
        const left = vm.x - half;
        const right = vm.x + half;
        const top = vm.y;
        const width = right - left;
        const height = chart.chartArea.bottom - top + borderWidth / 2 - 1;
        ctx.beginPath();
        ctx.lineWidth = borderWidth;
        ctx.strokeStyle = args.meta._dataset.borderColor;
        ctx.setLineDash(lineDash[args.meta._dataset.lineStyle]);
        ctx.moveTo(left, top);
        ctx.lineTo(left, top + height);
        ctx.moveTo(left, top);
        ctx.lineTo(left + width, top);
        ctx.moveTo(left + width, top);
        ctx.lineTo(left + width, top + height);
        ctx.stroke();
        ctx.save();
      });
    }
  },
};

const convertHexToRGBA = (hexCode, opacity) => {
  if (!hexCode) {
    return `rgba(0,0,0,${opacity || 1})`;
  }
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r},${g},${b},${opacity})`;
};

const lineStyles = {
  0: [0, 0],
  1: [0, 5],
  2: [5, 5],
  3: [0, 5, 5, 5],
  4: [20, 5],
  5: [0, 5, 20, 5],
};

const pointStyles = {
  0: "circle",
  1: "circle",
  2: "rectRot",
  3: "circle",
  4: "rectRot",
  5: "rect",
};

const markerPosition = {
  LEFT: "before",
  RIGHT: "after",
  CENTER: "middle",
};

const getBackgroundOpacity = (fillType, style, opacity) => {
  if (style === "COLUMN_SERIES" && fillType === "NONE") return 0;
  if (style === "COLUMN_SERIES" && fillType === "SOLID") return opacity / 100;
  if (fillType === "GRADIENT") return 0.2;
  return 1;
};

const getScales = (datasets) => {
  if (datasets && datasets.length > 0) {
    const barCharts = datasets.filter((item) => item.type === "bar");

    if (!barCharts.length) return undefined;

    if (barCharts[barCharts.length - 1].columnPlacement === "BETWEEN_TICKS") {
      return {
        x: {
          ticks: {
            labelOffset: -25,
            align: "end",
          },
        },
      };
    }
  }
};

const isCustomLineColumn = (style, lineType) => {
  if (style === "COLUMN_SERIES" && ["1", "2", "3", "4", "5"].includes(lineType))
    return true;
  return false;
};

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
    maxHeight: "80vh",
    overflowY: "auto",
  };
}

function ColumnChart(props) {
  const [modalStyle] = React.useState(getModalStyle);
  const {
    chartRef,
    showLegend,
    dashboardId,
    token,
    dashboardParameters,
    timeRange,
    height,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    onZoomInSlider,
    onZoomOutSlider,
    masterTimeRange,
    showGridlines = true,
  } = props;
  const [generatedData, setGeneratedData] = React.useState({
    datasets: [],
    labels: [],
  });
  const [chartData, setChartData] = React.useState({
    datasets: [],
    labels: [],
  });
  const [slidesData, setSlidesData] = React.useState({
    min: null,
    max: null,
    start: null,
    end: null,
    minRange: 5 * 1000 * 60,
  });
  const [language] = useLanguage();
  const [annotations, setAnnotations] = React.useState([]);
  const [minCachedDate, setMinCachedDate] = useState(0);
  const [maxCachedDate, setMaxCachedDate] = useState(0);
  const [cachedData, setCachedData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [renderSlider, setRenderSlider] = useState(false);
  const [showSliderModal, setShowSliderModal] = useState(false);
  const { width, ref: sliderContainerRef } = useResizeDetector();
  const chartRangeState = useSelector((state) => state?.chartRange);
  const dispatch = useDispatch();

  const getLabelRangeLimit = (label, length) => {
    if (!moment(label, "DD-MM-YYYY HH:mm").isValid()) {
      return "";
    }

    const formattedStartTime = moment(startTime).format("DD-MM-YYYY");
    const formattedEndTime = moment(endTime).format("DD-MM-YYYY");
    const formattedLabel = moment(label, "DD-MM-YYYY HH:mm");

    const hoursDiff = Math.abs(
      moment(formattedStartTime, "DD-MM-YYYY").diff(
        moment(formattedEndTime, "DD-MM-YYYY"),
        "hours"
      )
    );

    const diff = Math.abs(
      moment(formattedStartTime, "DD-MM-YYYY").diff(
        moment(formattedEndTime, "DD-MM-YYYY"),
        "days"
      )
    );

    const isSameYear = moment(formattedStartTime, "DD-MM-YYYY").isSame(
      moment(formattedEndTime, "DD-MM-YYYY"),
      "year"
    );

    if (hoursDiff <= 24) {
      return formattedLabel.format("DD-MM HH:mm");
    } else {
      if (diff < 10) {
        return formattedLabel.format("DD-MM HH:mm");
      } else {
        if (isSameYear) {
          return formattedLabel.format("DD-MM");
        } else {
          return formattedLabel.format("DD-MM-YYYY");
        }
      }
    }
  };

  const options = {
    plugins: {
      legend: {
        display: showLegend,
        labels: {
          generateLabels: function (chart) {
            const datasets = chart.data.datasets;

            const legend = datasets?.map(function (dataset, index) {
              return {
                datasetIndex: index,
                fillStyle: dataset.backgroundColor,
                lineDash: lineDash[dataset.lineStyle],
                lineWidth: dataset.isCustomLineColumn ? 1 : dataset.borderWidth,
                strokeStyle: dataset.borderColor,
                text: dataset.label,
                hidden: !chart.isDatasetVisible(index),
              };
            });

            return legend;
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem, data) {
            let param = dashboardParameters.find(
              (item) =>
                item.name_en === tooltipItem?.dataset?.label ||
                item.name_vi === tooltipItem?.dataset?.label
            );
            const value = isNaN(tooltipItem?.raw)
              ? tooltipItem?.raw?.y
              : tooltipItem?.raw;
            return formatString(param?.parameter?.parameter_format_text, value);
          },
        },
      },
      annotation: {
        annotations,
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: false,
          },
          mode: "xy",
        },
      },
    },

    hover: {
      mode: null,
    },
    // scales: getScales(chartData.datasets),
    scales: {
      x: {
        grid: {
          display: showGridlines,
        },
        ticks: {
          autoSkip: false,
          // minRotation: 90,
          callback: function (val, index, ticks) {
            let points = [];
            let okPoint = 2 * Math.floor(ticks?.length / 2) + 1;

            for (let i = 0; i < 9; i++) {
              points.push(Math.ceil((okPoint / 9) * i));
            }

            if (index === 0 || index === ticks?.length - 1) {
              return getLabelRangeLimit(
                this.getLabelForValue(val),
                ticks?.length
              );
            } else {
              if (ticks?.length < 30) {
                return getLabelRangeLimit(
                  this.getLabelForValue(val),
                  ticks?.length
                );
              } else {
                if (points.indexOf(index) > -1) {
                  return getLabelRangeLimit(
                    this.getLabelForValue(val),
                    ticks?.length
                  );
                } else {
                  return "";
                }
              }
            }
          },
        },
      },
      y: {
        grid: {
          display: showGridlines,
        },
      },
    },
  };

  const generateDatasets = (data) => {
    return Object.values(data)?.map((item) => {
      return {
        type: item["style"] === "LINE_SERIES" ? "line" : "bar",
        label: !_.isEmpty(item[`name_${language}`])
          ? item[`name_${language}`]
          : item["name_vi"],
        data: item?.values
          ? Object.keys(item?.values).map((key) => ({
              x: key,
              y: item?.values[key],
            }))
          : [],
        backgroundColor: convertHexToRGBA(
          item["main_color"],
          getBackgroundOpacity(
            item["fill_type"],
            item["style"],
            Number(item["fill_opacity"])
          )
        ),
        borderColor: item["main_color"],
        tension: item["line_type"] === "SPLINE" ? 0.4 : 0,
        stepped:
          item["line_type"] === "STEP"
            ? markerPosition[item["marker_position"]]
            : false,
        fill: item["fill_type"] === "SOLID" || item["fill_type"] === "GRADIENT",
        borderWidth: isCustomLineColumn(item["style"], item["line_style"])
          ? 0
          : item["line_width"],
        borderCapStyle:
          item["line_style"] === "1" ||
          item["line_style"] === "3" ||
          item["line_style"] === "5"
            ? "round"
            : "butt",
        borderDash: lineStyles[parseInt(item["line_style"])],
        pointRadius:
          parseInt(item["marker_type"]) === 0
            ? 0
            : parseInt(item["marker_size"]),
        pointHoverRadius: 2,
        pointStyle: pointStyles[parseInt(item["marker_type"])],
        pointBorderColor: item["main_color"],
        pointBackgroundColor:
          parseInt(item["marker_type"]) === 3 ||
          parseInt(item["marker_type"]) === 4 ||
          parseInt(item["marker_type"]) === 5
            ? "rgba(0, 0, 0, 0)"
            : item["main_color"],
        grouped: item["column_type"] === "STACKED" ? false : true,
        isCustomLineColumn: isCustomLineColumn(
          item["style"],
          item["line_style"]
        ),
        realBorderWidth: item["line_width"],
        lineStyle: item["line_style"],
        columnPlacement: item["column_placement"],
        order: Number(item["index"]),
      };
    });
  };

  const generateData = (data) => {
    let sets = [];

    Object?.values(data)?.forEach((item) => {
      Object.keys(item?.values)?.forEach((key) => {
        sets.push({
          key,
          value: item?.values?.[key],
        });
      });
    });

    return {
      // labels: [...new Set(_.orderBy(sets, "key")?.map((item) => item?.key))],
      labels: [
        ...new Set(
          _.sortBy(sets, (set) => {
            return moment(set?.key, DATE_TIME_FORMAT).toDate();
          })?.map((item) => item?.key)
        ),
      ],
      datasets: generateDatasets(data),
    };
  };

  const fetchData = async (params) => {
    setStartTime(params.start);
    setEndTime(params.end);

    dispatch(
      setChartRange({
        chartId: dashboardId,
        chartData: {
          startTime: params?.start,
          endTime: params?.end,
        },
      })
    );

    getChartData(
      { start: params?.start, end: params?.end },
      minCachedDate,
      maxCachedDate
    );
  };

  const debounceFn = useCallback(_debounce(fetchData, 1000), []);

  const onRangeChange = async (params) => {
    debounceFn(params);
  };

  const fetchApiData = async (params) => {
    try {
      let res;
      if (props.isPublic) {
        res = await workspaceDashboard.getDashboardPublicDataWithDate(
          dashboardId,
          moment(new Date(params.start))
            .utc()
            .format(DATE_TIME_FORMAT_BACKEND_SIDE),
          moment(new Date(params.end))
            .utc()
            .format(DATE_TIME_FORMAT_BACKEND_SIDE)
        );
      } else {
        res = await workspaceDashboard.getDashboardDataWithDate(
          dashboardId,
          moment(new Date(params.start))
            .utc()
            .format(DATE_TIME_FORMAT_BACKEND_SIDE),
          moment(new Date(params.end))
            .utc()
            .format(DATE_TIME_FORMAT_BACKEND_SIDE)
        );
      }

      if (res?.data && res?.message !== "Invalid Parameter!") {
        const dashboardData = res?.data;
        const data = {};

        dashboardParameters?.forEach((item) => {
          const values = {};

          dashboardData?.parameterDatas
            ?.find(
              (elm) => elm?.parameterDto?.parameterId === item?.parameter_id
            )
            ?.dataPointDtos?.forEach((point) => {
              values[moment(point.eventTime).format(DATE_TIME_FORMAT)] =
                point.value;
            });

          data[item.parameter_id] = {
            ...item,
            values,
          };
        });

        return data;
      } else {
        return null;
      }
    } catch (error) {
      console.log(error);

      return null;
    }
  };

  const getChartData = async (
    params,
    inputMinCachedDate,
    inputMaxCachedDate
  ) => {
    if (!params.start || !params.end) return;

    setIsLoading(true);

    const output = await fetchApiData({
      start: params.start,
      end: params.end,
    });

    setIsLoading(false);

    if (output !== null) {
      const newChartData = generateData(output);

      if (
        newChartData?.datasets?.length > 0 &&
        newChartData?.labels?.length > 0
      ) {
        setChartData(newChartData);
      }
    }
  };

  // EFFECTS

  useEffect(() => {
    const timer = setTimeout(() => {
      // let newStartTime = chartRangeState?.[dashboardId]?.startTime;
      // let newEndTime = chartRangeState?.[dashboardId]?.endTime;

      // if (isNaN(newStartTime) || isNaN(newEndTime)) {
      //   newStartTime = moment(new Date(masterTimeRange?.startTime)).valueOf();
      //   newEndTime = moment(new Date(masterTimeRange?.endTime)).valueOf();
      // }

      // setStartTime(newStartTime);
      // setEndTime(newEndTime);

      setRenderSlider(true);
    }, 2000);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let inputStartTime = startTime;
    let inputEndTime = endTime;

    if (chartRangeState?.[dashboardId]) {
      let newStartTime = chartRangeState?.[dashboardId]?.startTime;
      let newEndTime = chartRangeState?.[dashboardId]?.endTime;

      if (isNaN(newStartTime) || isNaN(newEndTime)) {
        inputStartTime = moment(new Date(masterTimeRange?.startTime)).valueOf();
        inputEndTime = moment(new Date(masterTimeRange?.endTime)).valueOf();
      }
    }

    getChartData(
      { start: inputStartTime, end: inputEndTime },
      minCachedDate,
      maxCachedDate
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRangeState?.[dashboardId], startTime, endTime]);

  useEffect(() => {
    let newAnnotations = [];

    [
      ...new Set(
        Object.values(props["data"])
          ?.filter((item) => item?.state_thresholds !== "HIDDEN")
          ?.map((item) => item?.parameter?.data_alarm)
          .flat()
      ),
    ]
      ?.filter((item) => item?.raise_alarm)
      ?.forEach((item) => {
        if (item?.min_value && !item?.max_value) {
          newAnnotations.push({
            id: item.id,
            type: "line",
            mode: "horizontal",
            value: item?.min_value,
            scaleID: "y",
            borderWidth: 2,
            borderDash: [10, 5],
            borderColor: "rgb(255, 99, 132)",
            label: {
              enabled: true,
              content: item[`name_${language}`],
              position: "end",
              backgroundColor: "rgb(255, 99, 132)",
            },
          });
        }

        if (!item?.min_value && item?.max_value) {
          newAnnotations.push({
            id: item.id,
            type: "line",
            mode: "horizontal",
            value: item?.max_value,
            scaleID: "y",
            borderWidth: 2,
            borderDash: [10, 5],
            borderColor: "rgb(255, 99, 132)",
            label: {
              enabled: true,
              content: item[`name_${language}`],
              position: "end",
              backgroundColor: "rgb(255, 99, 132)",
            },
          });
        }

        if (item.min_value && item.max_value) {
          newAnnotations.push({
            id: `min_value ${item.id}`,
            type: "line",
            mode: "horizontal",
            value: item?.min_value,
            scaleID: "y",
            borderWidth: 2,
            borderDash: [10, 5],
            borderColor: "rgb(255, 99, 132)",
            label: {
              enabled: true,
              content: item[`name_${language}`],
              position: "end",
              backgroundColor: "rgb(255, 99, 132)",
            },
          });

          newAnnotations.push({
            id: `max_value ${item.id}`,
            type: "line",
            mode: "horizontal",
            value: item?.max_value,
            scaleID: "y",
            borderWidth: 2,
            borderDash: [10, 5],
            borderColor: "rgb(255, 99, 132)",
            label: {
              enabled: true,
              content: item[`name_${language}`],
              position: "end",
              backgroundColor: "rgb(255, 99, 132)",
            },
          });
        }

        if (!item.min_value && !item.max_value) {
          newAnnotations.push({
            id: `max_value ${item.id}`,
            type: "line",
            mode: "horizontal",
            value: item?.value,
            scaleID: "y",
            borderWidth: 2,
            borderDash: [10, 5],
            borderColor: "rgb(255, 99, 132)",
            label: {
              enabled: true,
              content: item[`name_${language}`],
              position: "end",
              backgroundColor: "rgb(255, 99, 132)",
            },
          });
        }
      });

    const newChartData = generateData(props["data"]);

    setGeneratedData(newChartData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props["data"]]);

  useEffect(() => {
    if (
      generatedData?.datasets?.length > 0 &&
      generatedData?.labels?.length > 0
    ) {
      setChartData(generatedData);
    }
  }, [generatedData]);

  useEffect(() => {
    const data = chartData?.labels;
    const minTime = moment(data?.[0], DATE_TIME_FORMAT);
    const maxTime = moment(data?.[data?.length - 1], DATE_TIME_FORMAT);

    setSlidesData((prevData) => ({
      ...prevData,
      min: minTime.valueOf(),
      max: maxTime.valueOf(),
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData?.datasets]);

  const chartToRender = useMemo(() => {
    return (
      <>
        {chartData && (
          <div className="chart-wrapper" style={{ position: "relative" }}>
            {/* https://github.com/reactchartjs/react-chartjs-2/issues/735 */}
            <Bar
              ref={(reference) => {
                if (chartRef) {
                  chartRef.current = reference;
                }
              }}
              data={chartData}
              plugins={[plugin]}
              options={options}
              redraw={true}
            />

            {isLoading && (
              <span style={styles.loader}>
                <CircularProgress />
              </span>
            )}
          </div>
        )}
      </>
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  return (
    <div>
      {/* {props['showRangeSelection'] && renderHeader()} */}

      {chartToRender}

      {!!renderSlider && !Number.isNaN(startTime) && !Number.isNaN(endTime) && (
        <div style={styles.zoomBar}>
          <span style={styles.zoomButton} onClick={onZoomOutSlider}>
            <ZoomOutIcon />
          </span>

          <div
            ref={sliderContainerRef}
            style={{ flex: 1, marginLeft: 10, marginRight: 10 }}
          >
            <Slider
              min={moment(new Date(timeRange.startTime)).valueOf()}
              max={moment(new Date(timeRange.endTime)).valueOf()}
              minRange={initial.minRange}
              start={startTime}
              end={endTime}
              onRangeChange={onRangeChange}
              width={width}
              onDoubleClickBar={() => {}}
            />
          </div>

          <span style={styles.zoomButton} onClick={onZoomInSlider}>
            <ZoomInIcon />
          </span>
        </div>
      )}

      {!!!renderSlider && (
        <div style={styles.sliderLoaderContainer}>
          <CircularProgress />
        </div>
      )}

      <p style={styles.timeRangeLabel}>
        {moment(new Date(timeRange.startTime)).format("DD-MM-YYYY, HH:mm")} -{" "}
        {moment(new Date(timeRange.endTime)).format("DD-MM-YYYY, HH:mm")}
      </p>
    </div>
  );
}

const styles = {
  loader: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    padding: "24px",
    borderRadius: "100%",
    border: "1px solid #eee",
  },
  zoomBar: {
    display: "flex",
    alignItems: "center",
  },
  zoomButton: {
    color: "white",
    background: "#3499fe",
    width: 30,
    height: 30,
    marginTop: 30,
    borderRadius: 3,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1px 4px",
  },
  timeRangeLabel: {
    textAlign: "center",
    fontWeight: "bold",
    marginTop: 24,
  },
  sliderLoaderContainer: {
    textAlign: "center",
    marginTop: 12,
  },
};

export default ColumnChart;
